/**
 * Maps order IDs (deploy unit / strategem / drone with spawn-like effects) to the card fetched for previews
 * (BattleUI hand, Strategy). Reverse index built for Created (dev) “who spawns this?”.
 *
 * Omitted (no engine spawn / grant wired yet): unit 25 Carrier of the Rot; unit 258 Failed Resurrection;
 * unit 152 Stasis Orb (immobilize only, no new order card).
 */
export const DEPLOY_SPAWN_UNIT_MAP = {
  5: { id: 10, name: 'Bio-Engineered Howler', customStats: { attack: 1, health: 1 }, faction_name: 'Asrah' },
  22: { id: 22, name: 'Swarm Queen', faction_name: 'Asrah' },
  41: { id: 44, name: 'Infected', customStats: { attack: 1, health: 1 }, faction_name: 'Asrah' },
  137: { id: 150, name: 'Shadow Strike', customStats: { attack: 2, health: 2, abilities: ['Blitz'] }, faction_name: 'League of Shadows' },
  159: { id: 155, name: 'Standard Combat Orb', customStats: { attack: 2, health: 1 }, faction_name: 'League of Shadows' },
  210: { id: 213, name: 'Nobleborn Sentinel', customStats: { attack: 0, health: 4, abilities: ['Protect'] }, faction_name: "Ra's Children" },
  239: { id: null, name: 'Random Unit', isRandom: true, source: 'Completed Orders' },
  248: { id: 260, name: 'Unfinished Mummy', customStats: { attack: 1, health: 1 }, faction_name: "Ra's Children" },
  307: { id: 329, name: 'Ironclad Tank', faction_name: 'Remnants of Humanity' },
  394: { id: 216, name: 'Doomed Defender', customStats: { attack: 2, health: 2, abilities: ['Protect'] }, faction_name: "Ra's Children" },
  382: { id: 148, name: 'Shadow Sniper', count: 3, faction_name: 'League of Shadows' },
  97: { id: 99, name: 'Stinger', customStats: { attack: 1, health: 1 }, count: 2, faction_name: 'Exolot Dominion' },
  164: { id: 180, name: 'Touch of darkness', faction_name: 'League of Shadows' },
  281: { id: 330, name: 'Steel Viper Tank', customStats: { attack: 5, health: 5, abilities: ['Protect'] }, faction_name: 'Remnants of Humanity' },
  288: { id: 334, name: 'Close Quarters Blaster', faction_name: 'Remnants of Humanity' },
  286: { id: 373, name: 'Exoblade', faction_name: 'Exolot Dominion' },
  2623: { id: 373, name: 'Exoblade', faction_name: 'Exolot Dominion' },
  1099: { id: 373, name: 'Exoblade', faction_name: 'Exolot Dominion' },
  327: { id: 318, name: 'Venom Cloud', faction_name: 'League of Shadows' },
  287: { id: 317, name: 'Guided Missile', faction_name: 'Remnants of Humanity' },
  386: { id: 268, name: "Johnson's Desciple", faction_name: 'Remnants of Humanity' },
  /** Sha — deploy/fell grants random plague 219|220|221|247; preview uses 219. */
  201: { id: 219, faction_name: "Ra's Children" },
  /** Tatenen — turn end deploys 245 or 246 by intel; preview low priest. */
  231: { id: 245, name: 'Tatenen Low Priest', customStats: { attack: 1, health: 1 }, faction_name: 'The Ennead' },
  /** Embalm strategem — Counter Strike converts target to 2/2 Mummy (259). */
  226: { id: 259, name: 'Mummy', customStats: { attack: 2, health: 2 }, faction_name: 'Undead' },
  /** Forbidden Clone Protocol — 3/2 Animated Fleshsack (328) per zone. */
  324: { id: 328, name: 'Animated Fleshsack', customStats: { attack: 3, health: 2 }, faction_name: 'Undead' },
  /** Skyfire Strike drone — after 5 damage to general, deploy Sovereign Class Tank (331). */
  275: { id: 331, name: 'Sovereign Class Tank', faction_name: 'Remnants of Humanity' },
  /** Phaseform Strike Unit — Redeem deploys Phaseform Brawler (149). */
  138: { id: 149, name: 'Phaseform Brawler', faction_name: 'Caropaera' },
  /** Rift Tyrant Sul'ma — Redeem deploys Best Boy (135); stats from DB. */
  383: { id: 135, name: 'Best Boy', faction_name: 'Animal' },
  /** Urban Watcher — Redeem adds Energy Gun (335) to hand. */
  292: { id: 335, name: 'Energy Gun', faction_name: 'Remnants of Humanity' },
  /** Battle Unit Alpha-Alpha-8 — on damage, adds Canon Fodder Unit 1 (362) to hand. */
  360: { id: 362, name: 'Canon Fodder Unit 1', customStats: { attack: 0, health: 3, abilities: ['Protect'] }, faction_name: 'The Sentients' },
};

let spawnSourceParentIdsBySpawnId = null;

function addReverseParent(spawnId, parentOrderId) {
  const sid = Number(spawnId);
  const pid = Number(parentOrderId);
  if (!Number.isFinite(sid) || !Number.isFinite(pid)) return;
  const list = spawnSourceParentIdsBySpawnId.get(sid) || [];
  if (!list.includes(pid)) list.push(pid);
  spawnSourceParentIdsBySpawnId.set(sid, list);
}

function buildSpawnSourceReverseIndex() {
  if (spawnSourceParentIdsBySpawnId) return;
  spawnSourceParentIdsBySpawnId = new Map();
  for (const [parentKey, info] of Object.entries(DEPLOY_SPAWN_UNIT_MAP)) {
    const parentOrderId = Number(parentKey);
    if (!info || info.isRandom || info.id == null) continue;
    const spawnId = Number(info.id);
    if (!Number.isFinite(spawnId)) continue;
    addReverseParent(spawnId, parentOrderId);
  }
  /** Tatenen High Priest (246) same parent unit as Low Priest (245). */
  addReverseParent(246, 231);
  /** Remaining Plagues of Egypt from Sha (201); forward preview uses 219 only. */
  addReverseParent(220, 201);
  addReverseParent(221, 201);
  addReverseParent(247, 201);
  for (const list of spawnSourceParentIdsBySpawnId.values()) {
    list.sort((a, b) => a - b);
  }
}

/**
 * Strategy Created (dev): for a spawned unit / weapon order id, which deploy orders grant or spawn it.
 * IDs sorted ascending; empty if this order is not a known spawn target in the forward map.
 */
export function getSpawnSourceParentOrderIds(spawnedUnitOrderId) {
  buildSpawnSourceReverseIndex();
  const sid = Number(spawnedUnitOrderId);
  if (!Number.isFinite(sid)) return [];
  const list = spawnSourceParentIdsBySpawnId.get(sid);
  return list ? [...list] : [];
}

export function getDeployedUnitInfo(orderId) {
  const id = Number(orderId);
  return DEPLOY_SPAWN_UNIT_MAP[id] ?? null;
}
